import React, { useState, useEffect } from 'react';
import './GroupVoteCard.css';
import { getAuth } from 'firebase/auth';
import Cookies from 'js-cookie';

const GroupVoteCard = ({ userIds, communityId, conversationId }) => {
  const [profiles, setProfiles] = useState([]);
  const [votes, setVotes] = useState({});
  const [personalMessages, setPersonalMessages] = useState({});
  const [pendingInterest, setPendingInterest] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userIds?.length) return;
    const fetchProfiles = async () => {
      try {
        const auth = getAuth();
        const token = await auth.currentUser.getIdToken();
        const csrfToken = Cookies.get('mytoken');
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/userProfile/getProfilesToDisplay`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...(csrfToken && { 'X-CSRF-Token': csrfToken })
          },
          body: JSON.stringify({ userIds, communityId })
        });
        if (!res.ok) throw new Error('Failed to fetch profiles');
        const data = await res.json();
        setProfiles(data);
      } catch (err) {
        console.error('[GroupVoteCard] fetchProfiles error:', err);
      }
    };
    fetchProfiles();
  }, [userIds, communityId]);

  const handleInterestedClick = (userId) => {
    // Show message box before recording vote
    setPendingInterest(prev => ({ ...prev, [userId]: true }));
  };

  const handleSendVote = async (suggestedUserId) => {
    setLoading(true);
    try {
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken(true);
      const csrfToken = Cookies.get('mytoken');
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/group-match/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(csrfToken && { 'X-CSRF-Token': csrfToken })
        },
        body: JSON.stringify({
          conversationId,
          suggestedUserId,
          vote: 'interested',
          personalMessage: personalMessages[suggestedUserId] || ''
        })
      });
      if (!res.ok) throw new Error('Vote failed');
      const data = await res.json();
      setVotes(prev => ({ ...prev, [suggestedUserId]: { myVote: 'interested', ...data } }));
      setPendingInterest(prev => ({ ...prev, [suggestedUserId]: false }));
    } catch (err) {
      console.error('[GroupVoteCard] vote error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePassVote = async (suggestedUserId) => {
    setLoading(true);
    try {
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken(true);
      const csrfToken = Cookies.get('mytoken');
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/group-match/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(csrfToken && { 'X-CSRF-Token': csrfToken })
        },
        body: JSON.stringify({ conversationId, suggestedUserId, vote: 'pass', personalMessage: '' })
      });
      if (!res.ok) throw new Error('Vote failed');
      const data = await res.json();
      setVotes(prev => ({ ...prev, [suggestedUserId]: { myVote: 'pass', ...data } }));
    } catch (err) {
      console.error('[GroupVoteCard] vote error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!profiles.length) return <p className="gvc-loading">Loading suggestions...</p>;

  return (
    <div className="gvc-root">
      {profiles.map(profile => {
        const voteState = votes[profile.userId] || {};
        const voted = !!voteState.myVote;
        const awaitingMessage = pendingInterest[profile.userId];

        return (
          <div key={profile.userId} className={`gvc-card ${voted ? `gvc-card--voted-${voteState.myVote}` : ''}`}>
            <div className="gvc-profile">
              <img
                className="gvc-avatar"
                src={profile.pics?.[0] || 'https://cdn.pixabay.com/photo/2023/09/22/12/18/profile-8268938_640.png'}
                alt={profile.name}
              />
              <div className="gvc-info">
                <span className="gvc-name">{profile.name}</span>
                {profile.age && <span className="gvc-age">{profile.age}</span>}
                {profile.location && <p className="gvc-location">{profile.location}</p>}
              </div>
            </div>
            {profile.bio && <p className="gvc-bio">{profile.bio}</p>}

            {!voted && !awaitingMessage && (
              <div className="gvc-actions">
                <button
                  className="gvc-btn gvc-btn--interested"
                  onClick={() => handleInterestedClick(profile.userId)}
                  disabled={loading}
                >
                  Interested
                </button>
                <button
                  className="gvc-btn gvc-btn--pass"
                  onClick={() => handlePassVote(profile.userId)}
                  disabled={loading}
                >
                  Pass
                </button>
              </div>
            )}

            {awaitingMessage && !voted && (
              <div className="gvc-msg-box">
                <textarea
                  className="gvc-msg-input"
                  placeholder="Add a personal message to send with the invite (optional)..."
                  value={personalMessages[profile.userId] || ''}
                  onChange={e => setPersonalMessages(prev => ({ ...prev, [profile.userId]: e.target.value }))}
                  rows={2}
                  autoFocus
                />
                <div className="gvc-msg-actions">
                  <button
                    className="gvc-btn gvc-btn--send"
                    onClick={() => handleSendVote(profile.userId)}
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send invite'}
                  </button>
                  <button
                    className="gvc-btn gvc-btn--cancel"
                    onClick={() => setPendingInterest(prev => ({ ...prev, [profile.userId]: false }))}
                    disabled={loading}
                  >
                    Back
                  </button>
                </div>
              </div>
            )}

            {voted && (
              <div className="gvc-voted">
                {voteState.myVote === 'interested' ? (
                  <>
                    <span className="gvc-voted-label">You voted Interested</span>
                    <span className="gvc-tally">{voteState.interestedCount}/{voteState.totalVoters} interested</span>
                    {voteState.passed && <span className="gvc-passed">Invite sent!</span>}
                  </>
                ) : (
                  <span className="gvc-voted-label">You passed</span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default GroupVoteCard;
